import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import * as React from 'react';
import { useState } from 'react';
import BackendApiService from '../services/BackendApiService';
import { getAccessToken } from '../utils/accessTokenStore';
import { MultiError } from '../utils/errors';

type CancelRequestProps = {
  open: boolean;
  onClose: () => void;
  requestId: string;
  platform: string;
  name: string;
  resourceName: string;
  onSuccess?: () => void;
}

export default function CancelRequestDialog(props: CancelRequestProps) {
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [resultErrorMessage, setResultErrorMessage] = useState("");

  const handleCancel = () => {
    props.onClose();
  };

  const handleConfirmCancel = async () => {
    setResultErrorMessage("");
    setLoading(true);
    try {
      const accessToken = getAccessToken();
      const backendApiService = new BackendApiService(accessToken ?? '');
      await backendApiService.cancelModuleRequest(props.platform, props.name, props.requestId);
      if (props.onSuccess) {
        props.onSuccess();
      }
    } catch (err) {
      console.log("cancel error", err);
      if (err instanceof MultiError) {
        const combinedError = err.errors.join("\n");
        const looksLikeOutdatedBackend =
          combinedError.includes("property action has failed the following constraints: whitelistValidation") &&
          combinedError.includes("property moduleId has failed the following constraints: isString");

        if (looksLikeOutdatedBackend) {
          setResultErrorMessage("Cancel endpoint is not available in the running backend version. Please rebuild and restart backend/api-service (or deploy the backend version that includes cancel support).");
        } else {
          setResultErrorMessage(err.errors.join("\n"));
        }
      } else {
        setResultErrorMessage(`${err}`);
      }
    }
    setLoading(false);
    props.onClose();
    setSnackOpen(true);
  };

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle>Cancel Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this request for <strong>{props.resourceName}</strong>?
            The pull request will be abandoned and the branch will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">No, keep it</Button>
          <LoadingButton
            onClick={handleConfirmCancel}
            color="warning"
            loading={loading}
            variant="outlined"
          >
            Yes, cancel request
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackOpen} autoHideDuration={8000} onClose={() => setSnackOpen(false)}>
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={resultErrorMessage ? "error" : "success"}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {resultErrorMessage
            ? <>Error cancelling request: <br />{resultErrorMessage}</>
            : <>Request for {props.resourceName} was successfully cancelled</>
          }
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
